import { Controller } from "@hotwired/stimulus"

// Web Login Controller
// Handles verification code login flow with captcha integration
//
// Usage:
// <div data-controller="web-login"
//      data-web-login-api-url-value="/api/v1/verification_codes"
//      data-web-login-captcha-provider-value="tencent"
//      data-web-login-captcha-site-key-value="xxx"
//      data-web-login-resend-interval-value="60">
//
//   <input data-web-login-target="identifier" type="text">
//   <input data-web-login-target="code" type="text">
//   <button data-web-login-target="sendButton" data-action="click->web-login#sendCode">
//     Send Code
//   </button>
// </div>

export default class extends Controller {
  static targets = ["identifier", "code", "sendButton", "countdown", "error", "countryCode"]

  static values = {
    apiUrl: { type: String, default: "/api/v1/verification_codes" },
    captchaProvider: { type: String, default: "tencent" },
    captchaSiteKey: String,
    resendInterval: { type: Number, default: 60 },
    sendCodeText: { type: String, default: "发送验证码" },
    resendText: { type: String, default: "重新发送" }
  }

  connect() {
    this.countdown = 0
    this.captchaSessionToken = null
    this.loadCaptchaScript()
  }

  disconnect() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
    }
  }

  loadCaptchaScript() {
    if (this.captchaProviderValue === "tencent") {
      if (!window.TencentCaptcha) {
        const script = document.createElement('script')
        script.src = 'https://turing.captcha.qcloud.com/TJCaptcha.js'
        script.async = true
        document.head.appendChild(script)
      }
    } else if (this.captchaProviderValue === "turnstile") {
      if (!window.turnstile) {
        const script = document.createElement('script')
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
        script.async = true
        document.head.appendChild(script)
      }
    }
  }

  async sendCode(event) {
    event.preventDefault()

    // Validate identifier
    const rawIdentifier = this.identifierTarget.value.trim()
    if (!rawIdentifier) {
      this.showError("请输入手机号或邮箱")
      return
    }

    // Build full identifier (with country code for phone)
    const identifier = this.buildIdentifier(rawIdentifier)

    // Check countdown
    if (this.countdown > 0) {
      return
    }

    // If we have a valid captcha session token, skip captcha
    if (this.captchaSessionToken) {
      await this.doSendCode(identifier, null, null)
      return
    }

    // Trigger captcha verification
    this.triggerCaptcha(identifier)
  }

  buildIdentifier(rawIdentifier) {
    // If country code selector exists, prepend it (phone login mode)
    if (this.hasCountryCodeTarget) {
      return `${this.countryCodeTarget.value}${rawIdentifier}`
    }
    // Email login mode - use as-is
    return rawIdentifier
  }

  triggerCaptcha(identifier) {
    if (this.captchaProviderValue === "tencent") {
      this.triggerTencentCaptcha(identifier)
    } else if (this.captchaProviderValue === "turnstile") {
      this.triggerTurnstileCaptcha(identifier)
    } else {
      // No captcha configured, send directly (dev mode)
      this.doSendCode(identifier, null, null)
    }
  }

  triggerTencentCaptcha(identifier) {
    if (!window.TencentCaptcha) {
      console.error('TencentCaptcha script not loaded')
      this.showError("验证码加载失败，请刷新页面重试")
      return
    }

    if (!this.captchaSiteKeyValue) {
      // No captcha configured, send directly
      this.doSendCode(identifier, null, null)
      return
    }

    const captcha = new window.TencentCaptcha(
      this.captchaSiteKeyValue,
      async (res) => {
        if (res.ret === 0) {
          await this.doSendCode(identifier, res.ticket, res.randstr)
        } else if (res.ret !== 2) {
          // ret === 2 means user cancelled, no error needed
          this.showError("验证失败，请重试")
        }
      },
      { needFeedBack: false }
    )

    captcha.show()
  }

  triggerTurnstileCaptcha(identifier) {
    if (!window.turnstile) {
      console.error('Turnstile script not loaded')
      this.showError("验证码加载失败，请刷新页面重试")
      return
    }

    if (!this.captchaSiteKeyValue) {
      // No captcha configured, send directly
      this.doSendCode(identifier, null, null)
      return
    }

    // Create a temporary container for Turnstile
    const container = document.createElement('div')
    container.id = 'turnstile-container-' + Date.now()
    container.style.cssText = 'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); z-index: 9999; background: white; padding: 20px; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.2);'

    // Add overlay
    const overlay = document.createElement('div')
    overlay.style.cssText = 'position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); z-index: 9998;'
    overlay.onclick = () => {
      document.body.removeChild(overlay)
      document.body.removeChild(container)
    }

    document.body.appendChild(overlay)
    document.body.appendChild(container)

    window.turnstile.render(container, {
      sitekey: this.captchaSiteKeyValue,
      callback: async (token) => {
        document.body.removeChild(overlay)
        document.body.removeChild(container)
        await this.doSendCode(identifier, token, '')
      },
      'error-callback': () => {
        document.body.removeChild(overlay)
        document.body.removeChild(container)
        this.showError("验证失败，请重试")
      }
    })
  }

  async doSendCode(identifier, ticket, randstr) {
    this.disableSendButton(true)

    try {
      const body = {
        identifier: identifier,
        purpose: "login"
      }

      if (ticket) {
        body.captcha_ticket = ticket
      }
      if (randstr) {
        body.captcha_randstr = randstr
      }
      if (this.captchaSessionToken) {
        body.captcha_session_token = this.captchaSessionToken
      }

      const response = await fetch(this.apiUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (response.ok) {
        // Success - save session token and start countdown
        if (data.captcha_session_token) {
          this.captchaSessionToken = data.captcha_session_token
          // Set timeout to clear token when it expires
          setTimeout(() => {
            this.captchaSessionToken = null
          }, (data.captcha_session_expires_in || 300) * 1000)
        }

        this.startCountdown(data.resend_interval || this.resendIntervalValue)
        this.hideError()
      } else {
        // Error
        const errorMessage = data.error?.message || data.message || "发送失败，请重试"
        this.showError(errorMessage)
        this.disableSendButton(false)
      }
    } catch (error) {
      console.error('Send code error:', error)
      this.showError("网络错误，请重试")
      this.disableSendButton(false)
    }
  }

  startCountdown(seconds) {
    this.countdown = seconds

    this.updateSendButtonText()

    this.countdownTimer = setInterval(() => {
      this.countdown--

      if (this.countdown <= 0) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
        this.disableSendButton(false)
      }

      this.updateSendButtonText()
    }, 1000)
  }

  updateSendButtonText() {
    if (!this.hasSendButtonTarget) return

    if (this.countdown > 0) {
      this.sendButtonTarget.textContent = `${this.countdown}s`
      this.sendButtonTarget.disabled = true
    } else {
      this.sendButtonTarget.textContent = this.captchaSessionToken ? this.resendTextValue : this.sendCodeTextValue
      this.sendButtonTarget.disabled = false
    }
  }

  disableSendButton(disabled) {
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = disabled
    }
  }

  showError(message) {
    if (this.hasErrorTarget) {
      const span = this.errorTarget.querySelector('span')
      if (span) {
        span.textContent = message
      }
      this.errorTarget.classList.remove('hidden')
    }
  }

  hideError() {
    if (this.hasErrorTarget) {
      this.errorTarget.classList.add('hidden')
    }
  }
}
